import { useQuery } from '@tanstack/react-query'
import { useSession } from '@/lib/auth-client'
import { trpc } from '@/lib/trpc'
import { useAdminMode } from '@/context/AdminModeContext'

export type WorkspaceRole =
  | 'ADMIN'
  | 'CONTRIBUTOR'
  | 'VIEWER'
  | 'AUDITOR'
  | 'EMPLOYEE'
  | 'OWNER'
  | null

interface WorkspacePermissions {
  // Role information
  role: WorkspaceRole
  isOwner: boolean
  isAdmin: boolean
  isEditor: boolean
  isReviewer: boolean
  isViewer: boolean
  isViewerRole: boolean
  canWorkspaceWrite: boolean
  isSiteAdmin: boolean
  isAdminMode: boolean
  isAuditor: boolean
  isEmployee: boolean
  complianceRoleIds: string[]
  // Capabilities (what user can do)
  canView: boolean
  canEdit: boolean
  canReview: boolean
  canManage: boolean
  canDelete: boolean
  canDeploy: boolean
  canConfigureDomains: boolean
  canManageGit: boolean
  canManageMembers: boolean
  canManageApiKeys: boolean
  canUpdateSettings: boolean

  // Loading state
  isLoading: boolean
}

/**
 * Hook to get workspace permissions for the current user
 *
 * @param workspaceId - The workspace ID to check permissions for
 * @returns WorkspacePermissions object with role and capabilities
 *
 * @example
 * ```tsx
 * const { canDelete, canEdit, role } = useWorkspacePermissions(workspaceId)
 *
 * if (!canDelete) {
 *   return null // Hide delete button
 * }
 * ```
 */
export function useWorkspacePermissions(
  workspaceId?: string
): WorkspacePermissions {
  const { data: session } = useSession()
  const { isAdminMode } = useAdminMode()

  const permissionsQuery = useQuery(
    trpc.permissions.getWorkspacePermissions.queryOptions(
      { workspaceId: workspaceId || '' },
      { enabled: !!workspaceId }
    )
  )

  const principalQuery = useQuery(
    trpc.permissions.getWorkspacePrincipal.queryOptions(
      { workspaceId: workspaceId || '' },
      { enabled: !!workspaceId }
    )
  )

  const isSiteAdmin =
    permissionsQuery.data?.isSiteAdmin || session?.user?.role === 'SITE_ADMIN'
  const canonicalRole = principalQuery.data?.canonicalRole ?? null
  const role: WorkspaceRole = canonicalRole as WorkspaceRole
  const permissions = permissionsQuery.data?.permissions ?? []

  // Role checks
  const isOwner = permissionsQuery.data?.isOwner || role === 'OWNER'
  const hasAdminAccess = isSiteAdmin && isAdminMode
  const isAdmin = role === 'ADMIN' || isOwner || hasAdminAccess
  const isReviewer = isAdmin
  const isEditor = role === 'CONTRIBUTOR' || isAdmin
  const isViewerRole = role === 'VIEWER'
  const isViewer = isViewerRole
  const canWorkspaceWrite = isEditor
  const isAuditor = role === 'AUDITOR'
  const isEmployee = role === 'EMPLOYEE'
  const complianceRoleIds = principalQuery.data?.complianceRoleIds ?? []

  // Compute capabilities
  const canView =
    permissions.includes('workspace.view' as any) || !!role || hasAdminAccess
  const canEdit =
    permissions.includes('document.edit' as any) ||
    permissions.includes('topic.edit' as any) ||
    isEditor ||
    hasAdminAccess
  const canReview = isReviewer || hasAdminAccess
  const canManage = isAdmin || hasAdminAccess

  // Admin-only capabilities
  const canDelete = permissions.includes('workspace.delete' as any) || canManage
  const canDeploy =
    permissions.includes('workspace.deploy_production' as any) || canManage
  const canConfigureDomains =
    permissions.includes('workspace.configure_domains' as any) || canManage
  const canManageGit =
    permissions.includes('workspace.configure_git' as any) || canManage
  const canManageMembers =
    permissions.includes('workspace.manage_members' as any) || canManage
  const canManageApiKeys =
    permissions.includes('workspace.manage_api_keys' as any) || canManage
  const canUpdateSettings =
    permissions.includes('workspace.edit_settings' as any) || canManage

  return {
    // Roles
    role,
    isOwner,
    isAdmin,
    isEditor,
    isReviewer,
    isViewer,
    isViewerRole,
    canWorkspaceWrite,
    isSiteAdmin,
    isAdminMode,
    isAuditor,
    isEmployee,
    complianceRoleIds,

    // Capabilities
    canView,
    canEdit,
    canReview,
    canManage,
    canDelete,
    canDeploy,
    canConfigureDomains,
    canManageGit,
    canManageMembers,
    canManageApiKeys,
    canUpdateSettings,

    // Loading
    isLoading: permissionsQuery.isLoading || principalQuery.isLoading,
  }
}
